/**
 * RunDetail — one dispatch, end to end.
 *
 * What it has to answer, in the order someone asks it:
 *
 *   "What went out?"        source keys, shape, window, row counts, artifacts
 *   "Did it arrive?"        the timeline: sent → delivered → bounced
 *   "Did anyone open it?"   the access log, which is also the audit trail
 *   "Kill the link."        the grants table, with the exact IST expiry and a revoke
 *
 * On grants (CONTRACT §1): the 7-day window is ours, held in `expires_at` on the grant
 * row, not in an S3 signature — a presigned URL minted by a Lambda dies with that
 * Lambda's credentials in about an hour, which is why none is ever emailed. Revoking is
 * therefore instant: the resolver checks `revoked_at` before it checks expiry, so the
 * very next click gets a 410. Re-issuing mints a NEW token and never extends an old one.
 *
 * The token itself is never stored and never returned by the API — only its sha256. So
 * this screen shows `…9f3a` and offers no "copy link": there is nothing to copy, by
 * design, and pretending otherwise would send an operator hunting for a control that
 * cannot exist.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Badge, { CoverageBadge, LinkBadge, StatusBadge, MisalignedBadge } from '@/components/Badge';
import Button from '@/components/Button';
import ConfirmDialog from '@/components/ConfirmDialog';
import { Checkbox } from '@/components/Field';
import { Card, PageHeader } from '@/components/Layout';
import Modal from '@/components/Modal';
import RunTimeline from '@/components/RunTimeline';
import Table, { type Column } from '@/components/Table';
import { useToast } from '@/components/Toast';
import EmptyState from '@/components/EmptyState';
import { api, errorMessage } from '@/lib/api';
import {
  coverageOf,
  coverageWindowLabel,
  describeWindow,
  formatBytes,
  formatCount,
  formatDuration,
  istDateRange,
  istDateTime,
  istDateTimeExact,
  linkState,
  misalignedNote,
  maskToken,
  pluralise,
  timeAgo,
  toDate,
} from '@/lib/format';
import { RUN_IN_FLIGHT, reportShapeInfo } from '@/lib/types';
import type { AccessRow, Client, Grant, Run, RunArtifact, SesEvent } from '@/lib/types';

const POLL_MS = 5000;
const ACCESS_PAGE = 100;

/**
 * The two buckets, each named once on this page. They are genuinely different — source
 * objects are read from the read-only export bucket, artifacts are written to ours — and
 * the page used to state one in a column header (on every row of every run) and the other
 * in a tooltip, which read as a contradiction.
 */
const SOURCE_BUCKET = 'neuron-report';
const ARTIFACT_BUCKET = 'nrd-artifacts-442228338612';

/**
 * The last two segments of an s3 key — `fusion/borrower_2026-09-15.csv`. Enough to tell
 * two artifacts of one run apart, as selectable text. The full key stays on the `title`
 * and in the Source rows above: a key is what an operator pastes into a ticket, so it is
 * never hover-only.
 */
function keyTail(key: string | null | undefined): string {
  if (!key) return '—';
  const parts = String(key).split('/').filter(Boolean);
  return parts.slice(-2).join('/') || String(key);
}

export default function RunDetail() {
  const { id } = useParams<{ id: string }>();
  const runId = id ?? '';
  const navigate = useNavigate();
  const toast = useToast();

  const [run, setRun] = useState<Run | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [grantTotal, setGrantTotal] = useState<number | null>(null);
  const [grantsTruncated, setGrantsTruncated] = useState(false);
  const [access, setAccess] = useState<AccessRow[]>([]);
  const [accessCursor, setAccessCursor] = useState<string | null>(null);
  // The API's total, not the loaded length. `Access log (100+)` reads as "about a
  // hundred" on a 400-day audit trail; the Links card 36 lines below already learned this.
  const [accessTotal, setAccessTotal] = useState<number | null>(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [sesEvents, setSesEvents] = useState<SesEvent[]>([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [loaded, setLoaded] = useState(false);

  // One reader for the whole screen: the callout, the window row and the timeline must
  // not be able to disagree about the same run.
  const coverage = coverageOf(run);

  const [revokeFor, setRevokeFor] = useState<Grant | null>(null);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendIds, setResendIds] = useState<string[]>([]);
  const [confirmResend, setConfirmResend] = useState(false);

  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    if (loaded) setRefreshing(true);
    else setLoading(true);

    const load = async (): Promise<void> => {
      try {
        const fetched = await api.runs.get(runId);
        if (cancelled) return;
        setRun(fetched);
        setError(null);
        setLoaded(true);

        // Everything below is supporting detail: one failing call must not blank the
        // page, so each lands independently.
        const [grantPage, accessPage, sesPage, clientResult] = await Promise.allSettled([
          api.runs.grants(runId, { limit: 200 }),
          api.runs.access(runId, { limit: ACCESS_PAGE }),
          api.runs.sesEvents(runId, { limit: 200 }),
          api.clients.get(fetched.client_id),
        ]);
        if (cancelled) return;

        if (grantPage.status === 'fulfilled') {
          setGrants(grantPage.value.items);
          setGrantTotal(grantPage.value.total ?? grantPage.value.items.length);
          setGrantsTruncated(Boolean(grantPage.value.truncated));
        }
        if (accessPage.status === 'fulfilled') {
          setAccess(accessPage.value.items);
          setAccessCursor(accessPage.value.cursor);
          setAccessTotal(accessPage.value.total ?? null);
        }
        if (sesPage.status === 'fulfilled') setSesEvents(sesPage.value.items);
        if (clientResult.status === 'fulfilled') setClient(clientResult.value);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err));
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // `loaded` is intentionally excluded: it only chooses skeleton vs in-place refresh.
  }, [runId, reloadKey]);

  const inFlight = run ? (RUN_IN_FLIGHT as readonly string[]).includes(run.status) : false;

  useEffect(() => {
    if (!inFlight) return;
    const timer = window.setInterval(() => setReloadKey((key) => key + 1), POLL_MS);
    return () => window.clearInterval(timer);
  }, [inFlight]);

  const loadMoreAccess = useCallback(async () => {
    if (!accessCursor || accessLoading) return;
    setAccessLoading(true);
    try {
      const next = await api.runs.access(runId, { cursor: accessCursor, limit: ACCESS_PAGE });
      setAccess((current) => [...current, ...next.items]);
      setAccessCursor(next.cursor);
      if (next.total !== undefined && next.total !== null) setAccessTotal(next.total);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setAccessLoading(false);
    }
  }, [accessCursor, accessLoading, runId, toast]);

  const accessByToken = useMemo(() => {
    // accessed_at is nrd-access's sort key: an ISO-8601 string with the IST offset, not an
    // epoch. Math.max() over a string yields NaN, which rendered as a missing "last
    // downloaded" tooltip rather than as an error — so it is parsed before it is compared.
    const counts = new Map<string, { total: number; last: number }>();
    for (const row of access) {
      const current = counts.get(row.token_hash);
      const at = toDate(row.accessed_at)?.getTime() ?? 0;
      counts.set(row.token_hash, {
        total: (current?.total ?? 0) + 1,
        last: Math.max(current?.last ?? 0, at),
      });
    }
    return counts;
  }, [access]);

  const revoke = async (): Promise<void> => {
    if (!revokeFor) return;
    const target = revokeFor;
    try {
      const updated = await api.grants.revoke(target.token_hash, {
        reason: 'Revoked from the dispatch console',
      });
      setGrants((current) =>
        current.map((grant) => (grant.token_hash === target.token_hash ? updated : grant)),
      );
      toast.success('Link revoked', {
        description: `${target.recipient_email ?? target.recipient_id} gets a 410 on the next click.`,
      });
      setRevokeFor(null);
    } catch (err) {
      toast.error(errorMessage(err));
      throw err;
    }
  };

  const resend = async (): Promise<void> => {
    if (!run) return;
    try {
      const outcome = await api.runs.resend(run.run_id, { recipient_ids: resendIds });
      toast.success('Resent', {
        description: `${pluralise(outcome.ses_message_ids.length, 'message')} handed to SES. New links were minted.`,
      });
      setConfirmResend(false);
      setResendOpen(false);
      setReloadKey((key) => key + 1);
    } catch (err) {
      toast.error(errorMessage(err));
      throw err;
    }
  };

  if (!loading && !run) {
    return (
      <div className="stack">
        <PageHeader
          title="Run"
          breadcrumbs={[{ label: 'Runs', to: '/runs' }, { label: runId }]}
        />
        <EmptyState
          tone="danger"
          title="Could not load this run"
          description={error ?? 'No run with that id.'}
          action={
            <Button variant="primary" onClick={() => setReloadKey((key) => key + 1)}>
              Retry
            </Button>
          }
          secondaryAction={<Button onClick={() => navigate('/runs')}>Back to runs</Button>}
        />
      </div>
    );
  }

  const artifactColumns: Column<RunArtifact>[] = [
    { key: 'kind', header: 'Kind', width: '120px', nowrap: true, render: (a) => a.kind },
    {
      key: 'key',
      // The header used to spend its width on a bucket name and an AWS account id that
      // are identical on every row of every run. The bucket is stated once, in the
      // footer below.
      header: 'Artifact',
      mono: true,
      wrap: true,
      render: (a) => <span title={a.s3_key}>{keyTail(a.s3_key)}</span>,
    },
    { key: 'rows', header: 'Rows', align: 'right', mono: true, width: '96px', render: (a) => formatCount(a.rows) },
    { key: 'bytes', header: 'Size', align: 'right', mono: true, width: '96px', render: (a) => formatBytes(a.bytes) },
  ];

  const grantColumns: Column<Grant>[] = [
    {
      key: 'state',
      header: 'Link',
      width: '104px',
      nowrap: true,
      // State word only. The countdown is stated ONCE, in Expires (IST) — this column and
      // that one used to print `Expires in 3 d` side by side.
      render: (grant) => <LinkBadge grant={grant} />,
    },
    {
      key: 'recipient',
      header: 'Recipient',
      render: (grant) => (
        <span className="cluster-tight">
          <span className="mono nowrap" title={grant.recipient_email ?? grant.recipient_id}>
            {grant.recipient_email ?? grant.recipient_id}
          </span>
          <Badge variant="plain" glyph={grant.kind === 'recording' ? '♪' : '↓'}>
            {grant.kind === 'recording' ? 'recording playback' : 'report download'}
          </Badge>
        </span>
      ),
    },
    {
      key: 'expires',
      header: 'Expires (IST)',
      nowrap: true,
      width: '176px',
      headerTitle: 'Our expires_at — exact, and independent of any S3 signature',
      render: (grant) => (
        <div className="stack-tight">
          <span className="num">{istDateTimeExact(grant.expires_at)}</span>
          <span className="xs dim">{linkState(grant).label}</span>
        </div>
      ),
    },
    {
      key: 'issued',
      header: 'Issued (IST)',
      nowrap: true,
      width: '150px',
      optional: true,
      render: (grant) => <span className="num">{istDateTime(grant.issued_at)}</span>,
    },
    {
      key: 'access',
      header: 'Opened',
      align: 'right',
      mono: true,
      width: '96px',
      render: (grant) => {
        const seen = accessByToken.get(grant.token_hash);
        const total = grant.access_count ?? seen?.total ?? 0;
        const last = grant.last_accessed_at ?? (seen?.last ? seen.last : null);
        return total === 0 ? (
          <span className="dim">never</span>
        ) : (
          <span title={last ? istDateTimeExact(last) : undefined}>
            {formatCount(total)}
            {last ? <span className="xs dim"> · {timeAgo(last)}</span> : null}
          </span>
        );
      },
    },
    {
      key: 'token',
      header: 'Token',
      width: '92px',
      mono: true,
      nowrap: true,
      headerTitle: 'sha256 of the token, last 4 — the token itself is never stored',
      render: (grant) => <span title="Only the hash is stored">{maskToken(grant.token_hash)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '96px',
      align: 'right',
      nowrap: true,
      render: (grant) =>
        grant.revoked_at ? (
          <span className="xs dim">revoked</span>
        ) : (
          <Button size="sm" variant="danger" onClick={() => setRevokeFor(grant)}>
            Revoke
          </Button>
        ),
    },
  ];

  const accessColumns: Column<AccessRow>[] = [
    {
      key: 'at',
      header: 'When (IST)',
      nowrap: true,
      width: '190px',
      render: (row) => <span className="num">{istDateTimeExact(row.accessed_at)}</span>,
    },
    {
      key: 'outcome',
      header: 'Outcome',
      width: '118px',
      nowrap: true,
      render: (row) =>
        row.outcome === 'ok' ? (
          <Badge tone="ok" glyph="↓" title="Redirected to a fresh 60-second presigned URL">
            downloaded
          </Badge>
        ) : row.outcome === 'expired' ? (
          <Badge tone="warn" glyph="◷" title="Past our expires_at — served a 410">
            expired
          </Badge>
        ) : (
          <Badge tone="danger" glyph="⊗" terminalBad title="Grant was revoked — served a 410">
            revoked
          </Badge>
        ),
    },
    { key: 'ip', header: 'IP', mono: true, width: '140px', nowrap: true, render: (row) => row.ip },
    {
      key: 'ua',
      header: 'User agent',
      // The one unconstrained column: it wraps rather than being cut, because telling a
      // scanner from a person is the whole use of this value.
      wrap: true,
      render: (row) => <span className="small dim">{row.user_agent}</span>,
    },
    {
      key: 'token',
      header: 'Token',
      mono: true,
      width: '92px',
      nowrap: true,
      render: (row) => maskToken(row.token_hash),
    },
  ];

  const sesColumns: Column<SesEvent>[] = [
    {
      key: 'at',
      header: 'When (IST)',
      nowrap: true,
      width: '190px',
      render: (event) => <span className="num">{istDateTimeExact(event.event_at)}</span>,
    },
    {
      key: 'type',
      header: 'Event',
      width: '150px',
      nowrap: true,
      render: (event) =>
        event.event_type === 'Delivery' ? (
          <Badge tone="ok" glyph="✓">
            Delivery
          </Badge>
        ) : event.event_type === 'Bounce' || event.event_type === 'Complaint' || event.event_type === 'Reject' ? (
          <Badge tone="danger" glyph="⊘" terminalBad>
            {event.event_type}
          </Badge>
        ) : event.event_type === 'DeliveryDelay' ? (
          <Badge tone="warn" glyph="◔">
            DeliveryDelay
          </Badge>
        ) : (
          <Badge tone="neutral" glyph="·">
            {event.event_type}
          </Badge>
        ),
    },
    {
      key: 'recipient',
      header: 'Recipient',
      mono: true,
      nowrap: true,
      width: '240px',
      render: (event) => <span title={event.recipient}>{event.recipient}</span>,
    },
    {
      key: 'detail',
      // A bounce diagnostic is an error, and the reason sits at the END of an SMTP string.
      // It wraps; it is never cut to a tooltip.
      header: 'Detail',
      wrap: true,
      render: (event) =>
        event.detail ? <span className="small">{event.detail}</span> : <span className="dim">—</span>,
    },
    {
      key: 'message',
      header: 'SES message',
      mono: true,
      width: '110px',
      nowrap: true,
      optional: true,
      render: (event) => <span title={event.message_id}>{maskToken(event.message_id)}</span>,
    },
  ];

  const liveGrants = grants.filter((grant) => linkState(grant).state !== 'expired' && !grant.revoked_at);

  /**
   * What was ASKED for, resolved to IST dates — one grammar, never the bare spec word.
   * The Coverage row below states what was actually GOT, and substitutes it for this when
   * the two differ; stating both in one form would be the drift this avoids.
   */
  const requestedWindow = !run
    ? '—'
    : run.window_from && run.window_to
      ? istDateRange(run.window_from, run.window_to)
      : (run.window_label ?? (run.window ? describeWindow(run.window) : '—'));

  // Measured, not asserted: a finished run gets its duration, an unfinished one gets how
  // long it has been going. `Date.now()` is re-read on every poll while a run is in flight.
  const startedMs = toDate(run?.started_at ?? null)?.getTime() ?? null;
  const finishedMs = toDate(run?.finished_at ?? null)?.getTime() ?? null;
  const elapsedSeconds =
    startedMs === null ? null : Math.max(0, ((finishedMs ?? Date.now()) - startedMs) / 1000);

  /**
   * ONE consequence banner, not five stacked callouts.
   *
   * Load error, failed, stale, part day and unranked dispositions could all stack above
   * the first card, three to six lines each, so on a failed part-day run the "What went
   * out" card — source keys, row counts, artifacts, the reason the page was opened —
   * started below the fold. And three simultaneous amber boxes make amber decorative,
   * which is exactly what rule 3 exists to prevent.
   *
   * The verdict is stated once, here. The detail folds into the card rows that already
   * carry it: coverage into Coverage (IST), the unranked list beside the dispositions
   * link. Every sentence below is unchanged — only its position moved.
   */
  const verdict: {
    tone: 'danger' | 'warn';
    glyph: string;
    headline: string;
    body: ReactNode;
  } | null = !run
    ? null
    : run.status === 'failed'
      ? {
          tone: 'danger',
          glyph: '✕',
          headline: 'This run failed. Nothing was delivered.',
          // In full, wrapping, selectable. An error is never clipped and never tooltip-only.
          body: run.error ? <span className="mono small">{run.error}</span> : null,
        }
      : run.status === 'stale'
        ? {
            tone: 'warn',
            glyph: '◈',
            headline: 'Stale — nothing was sent, and that was correct.',
            body: (
              <>
                <div>
                  {run.error ??
                    'The source object was missing, or older than the window it was supposed to cover (CONTRACT §8.3).'}
                </div>
                {coverage.isPartial ? <div>{coverage.summary}</div> : null}
              </>
            ),
          }
        : coverage.isPartial
          ? {
              tone: 'warn',
              glyph: '◧',
              headline: 'Part day — this file covers less than its dates claim.',
              body:
                run.deliver === false ? (
                  <>
                    <strong>Nothing was mailed.</strong> This is an internal download, and a
                    partial source is accepted for one: refusing it protects nobody, because the
                    file does not leave the building. Before forwarding it to anyone outside,
                    quote the coverage below.
                  </>
                ) : run.recipients.length > 0 ? (
                  <>
                    <strong>This went to the client.</strong> {client?.name ?? run.client_id}{' '}
                    accepts a partial window, so the coverage is stated in the subject, the body
                    and the report itself.
                  </>
                ) : (
                  <>Nobody received this run.</>
                ),
            }
          : null;

  return (
    <div className="stack">
      <PageHeader
        breadcrumbs={[{ label: 'Runs', to: '/runs' }, { label: runId }]}
        title={
          <span className="cluster">
            <span>Run</span>
            {/* `startedAt` lets the badge stop pulsing on a run that cannot still be
                running: past the stall threshold it renders static and warn, with the
                measured elapsed beside it. No new status value — the word is the word. */}
            {run ? <StatusBadge status={run.status} startedAt={run.started_at} /> : null}
          </span>
        }
        subtitle={
          run ? (
            <>
              <span className="mono">{run.run_id}</span> · {client?.name ?? run.client_id} ·{' '}
              started {istDateTime(run.started_at)}{' '}
              <span className="dim">({timeAgo(run.started_at)})</span>
            </>
          ) : (
            'Loading…'
          )
        }
        actions={
          <>
            <Button
              onClick={() => setReloadKey((key) => key + 1)}
              loading={refreshing}
              icon={<span aria-hidden="true">↻</span>}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              disabled={!run || run.status !== 'sent' || run.recipients.length === 0}
              title={
                run && run.status !== 'sent'
                  ? 'Only a run that was sent can be resent'
                  : 'Re-send the existing artifact and mint fresh links'
              }
              onClick={() => {
                setResendIds(run ? run.recipients.map((entry) => entry.recipient_id) : []);
                setResendOpen(true);
              }}
            >
              Resend
            </Button>
          </>
        }
      />

      {error && run ? (
        <div className="callout tone-warn" role="alert">
          <span className="callout-glyph" aria-hidden="true">
            ▲
          </span>
          <span>{error} Showing the last data that loaded.</span>
        </div>
      ) : null}

      {verdict ? (
        <div className={`callout tone-${verdict.tone}`} role="alert">
          <span className="callout-glyph" aria-hidden="true">
            {verdict.glyph}
          </span>
          <div className="stack-tight" style={{ minWidth: 0 }}>
            <div className="strong">{verdict.headline}</div>
            {verdict.body}
          </div>
        </div>
      ) : null}

      <Card title="What went out">
        {run ? (
          <dl className="kv">
            <dt>Client</dt>
            <dd>
              <Link to={`/clients/${run.client_id}`}>{client?.name ?? run.client_id}</Link>
              {client ? <span className="dim"> · {client.company_name}</span> : null}
            </dd>

            <dt>Schedule</dt>
            <dd>
              {run.schedule_id ? (
                <Link className="mono" to="/schedules">
                  {run.schedule_id}
                </Link>
              ) : (
                <span className="dim">ad hoc — not from a schedule</span>
              )}
              {run.triggered_by ? <span className="dim"> · by {run.triggered_by}</span> : null}
            </dd>

            {/* The relative-window note is invariant prose — the same sentence on every
                run — so it moves to the label's tooltip and the value keeps the row. */}
            <dt title="The dates this covered are the ones baked into the source key below — a relative window is resolved at fire time and is not re-resolved here.">
              Shape &amp; window
            </dt>
            <dd>
              {reportShapeInfo(run.report_shape).label} (
              {reportShapeInfo(run.report_shape).gloss}){' '}
              ·{' '}
              <span className="num" title={run.window ? describeWindow(run.window) : undefined}>
                {requestedWindow}
              </span>
              {run.format ? (
                <>
                  {' '}· <span className="mono">{run.format}</span>
                </>
              ) : null}
            </dd>

            <dt>Coverage (IST)</dt>
            <dd>
              <div className="stack-tight">
                {coverage.isPartial ? (
                  <>
                    <div className="cluster-tight">
                      <CoverageBadge coverage={coverage} />
                      <MisalignedBadge coverage={coverage} />
                    </div>
                    <div className="readback">
                      <span className="strong">
                        {coverageWindowLabel(coverage, run.window_from, run.window_to)}
                      </span>
                      <span className="dim"> · requested {requestedWindow}</span>
                    </div>
                    <div className="small">{coverage.summary}</div>
                    {/* The reasoning behind that clock. It never changes, so it collapses —
                        the words are the ones the coverage feature ships with. */}
                    <details className="small">
                      <summary className="dim">Where that time comes from</summary>
                      <div style={{ paddingTop: 'var(--s-1)' }}>
                        {coverage.coveredTo ? (
                          <>
                            The latest call in the source is{' '}
                            <span className="strong">{coverage.coveredToLabel}</span>. That is the
                            data, not the file&apos;s last-modified time, which runs later than its
                            contents and would overstate what is here.
                          </>
                        ) : (
                          <>
                            The source stops before the window closes. No coverage time was
                            reported with this run, so the console cannot say when.
                          </>
                        )}
                      </div>
                    </details>
                  </>
                ) : coverage.measured ? (
                  <>
                    <span>
                      Full window · last call in the source{' '}
                      <span className="strong">{coverage.coveredToLabel}</span>
                    </span>
                    <MisalignedBadge coverage={coverage} />
                  </>
                ) : (
                  <>
                    <span className="dim">Not measured</span>
                    <span className="xs dim">
                      This run reported no covered-to time, so nothing is known about where its
                      data stops. That is not the same as covering the whole window.
                    </span>
                  </>
                )}
                {coverage.rowsMisaligned !== null && coverage.rowsMisaligned > 0 ? (
                  <div className="small">{misalignedNote(coverage)}</div>
                ) : null}
              </div>
            </dd>

            <dt>Source</dt>
            <dd>
              {run.source_keys.length === 0 ? (
                <span className="dim">none resolved</span>
              ) : (
                <div className="stack-tight">
                  {/* The bucket is named once, here, rather than once per key. The keys
                      themselves are what an operator pastes into a ticket, so they are in
                      full and selectable. */}
                  <span className="xs dim mono">s3://{SOURCE_BUCKET}/ · read-only</span>
                  {run.source_keys.map((key) => (
                    <span key={key} className="mono small" title={`s3://${SOURCE_BUCKET}/${key}`}>
                      {key}
                    </span>
                  ))}
                </div>
              )}
            </dd>

            <dt>Rows</dt>
            <dd className="mono num">
              {formatCount(run.rows_in)} read → {formatCount(run.rows_out)} emitted
            </dd>

            {/* CONTRACT §8.4: the exact column list this client received. It was stored on
                the run for the audit view and rendered nowhere, so "which columns did they
                actually get?" cost a trip to another screen. Revealed in place — never in
                a tooltip. */}
            {run.columns && run.columns.length > 0 ? (
              <>
                <dt>Columns</dt>
                <dd>
                  <details>
                    <summary className="small">
                      {pluralise(run.columns.length, 'column')} emitted, in this order
                    </summary>
                    <div className="cluster" style={{ gap: 'var(--s-1)', paddingTop: 'var(--s-2)' }}>
                      {run.columns.map((column) => (
                        <code key={column} className="mono xs">
                          {column}
                        </code>
                      ))}
                    </div>
                  </details>
                </dd>
              </>
            ) : null}

            <dt>Recipients</dt>
            <dd>
              {run.recipients.length === 0 ? (
                <span className="dim">none</span>
              ) : (
                // One address per line, never broken mid-token. `.kv dd` sets
                // overflow-wrap: anywhere, which used to split addresses inside the local
                // part — unreadable, and unsafe to copy by eye.
                <div className="stack-tight">
                  {run.recipients.map((entry) => (
                    <span key={entry.recipient_id || entry.email} className="cluster-tight">
                      <span className="mono nowrap" style={{ overflowWrap: 'normal' }}>
                        {entry.email}
                      </span>
                      <Badge variant="plain">{entry.kind}</Badge>
                      {entry.status ? <span className="xs dim">{entry.status}</span> : null}
                    </span>
                  ))}
                </div>
              )}
            </dd>

            {/* Was a banner above the fold; it belongs beside the link that fixes it. */}
            {run.unranked_dispositions.length > 0 ? (
              <>
                <dt>Unranked</dt>
                <dd>
                  <div className="stack-tight" style={{ minWidth: 0 }}>
                    <div className="cluster-tight">
                      <Badge tone="warn" glyph="?">
                        {pluralise(run.unranked_dispositions.length, 'disposition')} unranked
                      </Badge>
                      <span className="small dim">
                        every borrower holding one sorts as if never contacted
                      </span>
                    </div>
                    {/* Disposition values are shown exactly as stored — no case-correcting,
                        no expanding, no spell-fixing (CONTRACT §5). */}
                    <div className="cluster" style={{ gap: 'var(--s-1)' }}>
                      {run.unranked_dispositions.map((value) => (
                        <code key={value} className="mono xs">
                          {value || '(blank)'}
                        </code>
                      ))}
                    </div>
                    <Link className="small" to={`/dispositions/${run.client_id}`}>
                      Rank them for {client?.name ?? run.client_id} →
                    </Link>
                  </div>
                </dd>
              </>
            ) : null}

            <dt>Finished</dt>
            <dd>
              {run.finished_at ? (
                <>
                  <span className="num">{istDateTimeExact(run.finished_at)}</span>
                  {elapsedSeconds !== null ? (
                    <>
                      {' · '}
                      <span className="num">{formatDuration(elapsedSeconds)}</span>
                    </>
                  ) : null}
                  <span className="dim"> · {timeAgo(run.finished_at)}</span>
                </>
              ) : (
                <>
                  {/* "still running" with no number is how a run that died overnight reads
                      as healthy. The elapsed time is measured, not asserted. */}
                  <span className="dim">still running</span>
                  {elapsedSeconds !== null ? (
                    <>
                      {' · '}
                      <span className="num">{formatDuration(elapsedSeconds)} so far</span>
                    </>
                  ) : null}
                </>
              )}
            </dd>
          </dl>
        ) : (
          <div className="cluster">
            <span className="spinner" aria-hidden="true" />
            <span className="muted">Loading run…</span>
          </div>
        )}
      </Card>

      {run && run.artifacts.length > 0 ? (
        <Card
          title="Artifacts"
          flush
          // The bucket, once, where it is a fact about the card rather than a fact about
          // every row of every run.
          footer={<span className="small dim mono">s3://{ARTIFACT_BUCKET}/</span>}
        >
          <Table
            columns={artifactColumns}
            rows={run.artifacts}
            rowKey={(artifact) => artifact.s3_key}
            dense
            flush
          />
        </Card>
      ) : null}

      <Card title="Timeline">
        {run ? (
          <RunTimeline
            run={run}
            grants={grants}
            grantTotal={grantTotal}
            grantsTruncated={grantsTruncated}
            access={access}
            sesEvents={sesEvents}
            loading={loading || refreshing}
          />
        ) : (
          <div className="cluster">
            <span className="spinner" aria-hidden="true" />
            <span className="muted">Loading…</span>
          </div>
        )}
      </Card>

      <Card
        // The count is the API's total, not what this page happens to hold. A run that
        // links every recording mints one grant per recording — showing `grants.length`
        // told an operator there were 50 links when there were 575.
        title={`Links issued (${(grantTotal ?? grants.length).toLocaleString('en-IN')})`}
        footer={
          grantsTruncated
            ? `Showing the most recent ${grants.length.toLocaleString('en-IN')} of ${(grantTotal ?? 0).toLocaleString('en-IN')}. More links exist than are listed here.`
            : undefined
        }
        actions={
          <span className="small dim">
            {liveGrants.length > 0
              ? `${pluralise(liveGrants.length, 'link')} still working`
              : 'nothing live'}
          </span>
        }
        flush
      >
        <Table
          columns={grantColumns}
          rows={grants}
          rowKey={(grant) => grant.token_hash}
          loading={loading && grants.length === 0}
          dense
          flush
          rowTone={(grant) => {
            const state = linkState(grant).state;
            return state === 'revoked' ? 'danger' : state === 'expiring' ? 'warn' : state === 'expired' ? 'muted' : 'default';
          }}
          empty={
            <EmptyState
              title="No links minted"
              description="Grants appear once the run reaches the sending stage. A run that never sent has none."
            />
          }
        />
      </Card>

      <Card
        // The API's total when there is one. `(100+)` reads as "about a hundred" on a
        // 400-day audit trail; when no total comes back the count is dropped and the
        // footer says in words that this is the most recent page of a longer list.
        title={
          accessTotal !== null
            ? `Access log (${formatCount(accessTotal)})`
            : accessCursor
              ? 'Access log'
              : `Access log (${formatCount(access.length)})`
        }
        actions={
          accessCursor ? (
            <Button size="sm" loading={accessLoading} onClick={() => void loadMoreAccess()}>
              Load more
            </Button>
          ) : null
        }
        flush
        footer={
          <span className="small dim">
            {accessTotal === null && accessCursor
              ? `Showing the most recent ${formatCount(access.length)} — more exist. `
              : ''}
            Kept for 400 days — longer than the grants themselves, because this is the audit trail.
          </span>
        }
      >
        <Table
          columns={accessColumns}
          rows={access}
          rowKey={(row) => `${row.token_hash}-${row.accessed_at}`}
          loading={loading && access.length === 0}
          dense
          flush
          rowTone={(row) => (row.outcome === 'ok' ? 'default' : row.outcome === 'expired' ? 'warn' : 'danger')}
          empty={
            <EmptyState
              title="Nobody has opened a link yet"
              description="Every click on /d/{token} lands here — who, when, from where, and what they got."
            />
          }
        />
      </Card>

      <Card title={`Delivery events (${sesEvents.length})`} flush>
        <Table
          columns={sesColumns}
          rows={sesEvents}
          // event_key is the row's own sort key and is unique per recipient within one
          // event; the timestamp is not — a bounce naming three addresses shares one.
          rowKey={(event) => `${event.message_id}-${event.event_key ?? event.event_at}`}
          loading={loading && sesEvents.length === 0}
          dense
          flush
          rowTone={(event) =>
            event.event_type === 'Bounce' || event.event_type === 'Complaint' || event.event_type === 'Reject'
              ? 'danger'
              : event.event_type === 'DeliveryDelay'
                ? 'warn'
                : 'default'
          }
          empty={
            <EmptyState
              title="No delivery events yet"
              description="SES reports delivery, bounces and complaints back through the event webhook."
            />
          }
        />
      </Card>

      <Modal
        open={resendOpen}
        onClose={() => setResendOpen(false)}
        size="md"
        title="Resend this report"
        description="Sends the artifact that already exists — nothing is regenerated and the source file is not re-read."
        footer={
          <>
            <Button variant="ghost" onClick={() => setResendOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={resendIds.length === 0}
              onClick={() => setConfirmResend(true)}
            >
              Resend to {pluralise(resendIds.length, 'recipient')}
            </Button>
          </>
        }
      >
        <div className="stack">
          <div className="callout tone-warn">
            <span className="callout-glyph" aria-hidden="true">
              ▲
            </span>
            <span>
              Each recipient gets a <strong>new</strong> token with a fresh{' '}
              {client?.link_ttl_days ?? 7}-day window. The links already in their inbox keep their
              original expiry — re-issuing never extends an old token (CONTRACT §1).
            </span>
          </div>

          <div className="stack-tight">
            {(run?.recipients ?? []).map((entry) => (
              <Checkbox
                key={entry.recipient_id || entry.email}
                checked={resendIds.includes(entry.recipient_id)}
                label={<span className="mono">{entry.email}</span>}
                hint={`${entry.kind}${entry.status ? ` · ${entry.status}` : ''}`}
                onChange={() =>
                  setResendIds((current) =>
                    current.includes(entry.recipient_id)
                      ? current.filter((value) => value !== entry.recipient_id)
                      : [...current, entry.recipient_id],
                  )
                }
              />
            ))}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmResend}
        tone="warn"
        title="Resend now?"
        confirmLabel={`Resend to ${pluralise(resendIds.length, 'recipient')}`}
        consequence={
          coverage.isPartial
            ? 'Email leaves immediately and cannot be recalled — carrying a part day file, not a whole one.'
            : 'Email leaves immediately and cannot be recalled.'
        }
        onCancel={() => setConfirmResend(false)}
        onConfirm={resend}
      >
        {/* A resend mails the file that was ALREADY built. It is not re-resolved and not
            re-read, so its coverage cannot improve between the original send and this one —
            a partial run stays partial however long you wait, and the only way to get the
            whole day is a fresh run against a later export. Saying that here is the point:
            a resend is the quiet route by which an unlabelled part-day reaches a client. */}
        {coverage.isPartial ? (
          <div className="stack-tight">
            <div className="cluster-tight">
              <CoverageBadge coverage={coverage} />
            </div>
            <div className="small">
              {coverage.summary} Resending does not re-read the source — this mails the same
              part day file, with the same coverage stated in it. For the whole day, run the
              window again once a later export has landed.
            </div>
          </div>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={revokeFor !== null}
        tone="danger"
        title="Revoke this link?"
        confirmLabel="Revoke link"
        consequence="The recipient gets a 410 on their very next click — the resolver checks revocation before expiry, so there is no propagation delay. Re-issuing mints a new token; it does not bring this one back."
        confirmPhrase={revokeFor?.recipient_email ?? revokeFor?.recipient_id}
        onCancel={() => setRevokeFor(null)}
        onConfirm={revoke}
      >
        <div className="stack-tight">
          <div>
            Token <span className="mono">{maskToken(revokeFor?.token_hash)}</span> ·{' '}
            {revokeFor?.kind === 'recording' ? 'recording playback' : 'report download'}
          </div>
          <div className="small dim">
            Currently {revokeFor ? linkState(revokeFor).label.toLowerCase() : ''}. The report file
            itself is not deleted — only the link dies.
          </div>
        </div>
      </ConfirmDialog>
    </div>
  );
}
